import { Inject, Injectable } from '@angular/core';
import { SalesTeamInterface } from '@vendasta/sales-common';
import { Observable, ReplaySubject, combineLatest } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { SalesTeamOption } from '../filter-options/sales-team-option.model';
import { MARKET_ID_TOKEN, PARTNER_ID_TOKEN, SALES_TEAMS_SERVICE_TOKEN, SalesTeamsService } from './tokens';

interface SalesTeamMultiSelectStore {
  selectableSalesTeams$: Observable<SalesTeamOption[]>;
  selectedSalesTeams$: Observable<SalesTeamOption[]>;

  setSelectedSalesTeams(salesTeamsOptions: string[]): void;
}

@Injectable()
export class SalesTeamMultiSelectStoreService implements SalesTeamMultiSelectStore {
  readonly selectableSalesTeams$: Observable<SalesTeamOption[]>;
  private readonly selectedSalesTeams$$: ReplaySubject<SalesTeamOption[]> = new ReplaySubject(1);
  readonly selectedSalesTeams$: Observable<SalesTeamOption[]> = this.selectedSalesTeams$$.pipe(filter((v) => !!v));
  readonly selectedTeamsSalespeopleIds$: Observable<string[]>;
  readonly salesTeams$: Observable<SalesTeamInterface[]> = combineLatest([this.partnerId$, this.marketId$]).pipe(
    switchMap(([partnerId, marketId]) => this.salesTeamService.getSalesTeams$(partnerId, marketId)),
  );

  constructor(
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
    @Inject(MARKET_ID_TOKEN) private readonly marketId$: Observable<string>,
    @Inject(SALES_TEAMS_SERVICE_TOKEN) private readonly salesTeamService: SalesTeamsService,
  ) {
    this.selectableSalesTeams$ = this.salesTeams$.pipe(
      map((st) => st.map((s) => new SalesTeamOption({ groupId: s.groupId, teamName: s.teamName }))),
    );

    this.selectedSalesTeams$.subscribe((salesTeams) => this.fetchSalesTeamsSalespeopleIds(salesTeams.map((t) => t.id)));

    this.selectedTeamsSalespeopleIds$ = this.getSalesTeamsSalesPeopleIds$();
  }

  private getSalesTeamsSalesPeopleIds$(): Observable<string[]> {
    return this.salesTeamService.arrayOfSalesTeamMembers$.pipe(
      map((teams) => {
        const salespeopleIds: string[] = [];

        if (teams) {
          for (const team of teams) {
            salespeopleIds.push(...team);
          }
        }

        return salespeopleIds;
      }),
    );
  }

  private fetchSalesTeamsSalespeopleIds(salesTeamIds: string[]): void {
    this.salesTeamService.salesTeamIdsToArrayOfSalesPeopleIds(salesTeamIds);
  }

  getSalesTeamOptionsFromIds$(ids: string[]): Observable<string[]> {
    return this.selectableSalesTeams$.pipe(
      map((st) => st.filter((sp) => ids.includes(sp.id))),
      map((st) => st.map((sp) => `${sp.id}:${sp.teamName}`)),
    );
  }

  /**
   * @param salesTeamsOptions - array of `${salesTeamId}:${salesTeamName}` strings
   */
  setSelectedSalesTeams(salesTeamsOptions: string[]): void {
    const salesTeams = (salesTeamsOptions || []).map((o) => SalesTeamOption.fromOptionValue(o));
    this.selectedSalesTeams$$.next(salesTeams);
  }
}
